import React, { createContext, useContext, useRef } from "react"
import { create, useStore } from "zustand"
import { consultationReportFragment$key } from "@/modules/patients/detail/consultation-report/services/__generated__/consultationReportFragment.graphql"
import type { TimeToTreatType } from "@/types/__generated__/union-types"

type State = {
  consultationReportRef: consultationReportFragment$key | null
  consultationReportTimeToTreat: TimeToTreatType | null
}

type Actions = {
  setConsultationReportRef: (ref: consultationReportFragment$key | null) => void
  setConsultationReportTimeToTreat: (value: TimeToTreatType | null) => void
}

const initialState: State = {
  consultationReportRef: null,
  consultationReportTimeToTreat: null,
}

const createConsultationReportStore = () => {
  return create<State & Actions>((set) => ({
    ...initialState,
    setConsultationReportRef: (ref) => set({ consultationReportRef: ref }),
    setConsultationReportTimeToTreat: (v) => set({ consultationReportTimeToTreat: v }),
  }))
}

type ConsultationReportStoreType = ReturnType<typeof createConsultationReportStore>

const ConsultationReportStoreContext = createContext<ConsultationReportStoreType | null>(null)

type ConsultationReportStoreProviderProps = React.PropsWithChildren

export const ConsultationReportStoreProvider = ({ children }: ConsultationReportStoreProviderProps) => {
  const storeRef = useRef<ConsultationReportStoreType>(undefined)
  if (!storeRef.current) {
    storeRef.current = createConsultationReportStore()
  }
  return (
    <ConsultationReportStoreContext.Provider value={storeRef.current}>
      {children}
    </ConsultationReportStoreContext.Provider>
  )
}

export function useConsultationReportStoreContext<T>(selector: (state: State & Actions) => T): T {
  const store = useContext(ConsultationReportStoreContext)
  if (!store) throw new Error("Missing ConsultationReportStoreProvider in the tree")
  return useStore(store, selector)
}
